import React from "react";
import { Col } from "react-bootstrap";
import Form from "../../../assets/vfx/form/form";
import Control from "../../../assets/vfx/controls/control";
import {
  IControlStyles,
  PageContent,
  useControlStyles,
} from "../../../assets/layouts";
import {
  SearchDto,
  SearchFilter,
  SearchOperator,
} from "../../../assets/utils/lib/searchDto";

import { Constants } from "../../../assets/utils/lib/constants";
import {
  defaultSearchQuery,
  GRID_ACTION_TYPES,
  SetState,
} from "../../../assets/types";
import { ControlDataGridColumnProps } from "../../../assets/vfx/controls/components/datagrid/controlDataGrid";
import { GridRssFeedDto } from "../../../services/dto";
import {
  createActionColumn,
  createLayoutOptions,
} from "../../../assets/configuration/gridConfig";
import { SearchContainer } from "../../../assets/vfx/search/searchContainer";
import { RssFeedLookupProps } from "./rssFeedForm";
import { RssFeedService } from "./rssFeedService";
import { getRssFeedOptions } from "./rssFeedUtils";

interface RssFeedPageProps {
  controlStyles?: IControlStyles;
  onCustomEvent?: (actionType: string, dataId: string) => void;
  lookups?: RssFeedLookupProps;
  setCommunity: SetState<string | undefined>;
  setRssFeedType: SetState<string | undefined>;
  title: string;
  communityId?: string;
  rssFeedTypeId?: string;
  hideTitle?: boolean;
}

export interface RssFeedSearchProps {
  name: string;
  communityId: string;
  rssFeedTypeId?: string;
}

const DATA_ID_FIELD = "id";

const gridColumns: ControlDataGridColumnProps[] = [
  {
    data: DATA_ID_FIELD,
    title: "ID",
    visible: false,
    columnType: "number",
    alignment: "right",
    sortable: false,
  },
  {
    data: "name",
    title: "Name",
    visible: true,
    columnType: "text",
    alignment: "left",
    width: "25%",
  },
  {
    data: "description",
    title: "Description",
    visible: true,
    columnType: "text",
    alignment: "left",
    width: "25%",
  },
  {
    data: "rssFeedType",
    title: "RSS Feed Type",
    visible: true,
    columnType: "text",
    alignment: "left",
    width: "18%",
  },
  {
    data: "community",
    title: "Community",
    visible: true,
    columnType: "text",
    alignment: "left",
    width: "30%",
  },
];

export const RssFeedPage = (props: RssFeedPageProps) => {
  const baseFiltersFromProps = React.useMemo<SearchFilter[]>(() => {
    const filters: SearchFilter[] = [];
    if (props.communityId) {
      filters.push({
        field: "communityId",
        operator: SearchOperator.Equal,
        value: props.communityId,
      });
    }
    if (props.rssFeedTypeId) {
      filters.push({
        field: "rssFeedTypeId",
        operator: SearchOperator.Equal,
        value: props.rssFeedTypeId,
      });
    }
    return filters;
  }, [props.communityId, props.rssFeedTypeId]);

  const [searchQuery, setSearchQuery] = React.useState<SearchDto>(() => ({
    ...defaultSearchQuery,
    filters: [...defaultSearchQuery.filters, ...baseFiltersFromProps],
  }));

  React.useEffect(() => {
    setSearchQuery((prev) => {
      const withoutPropFilters = prev.filters.filter(
        (f) => f.field !== "communityId" && f.field !== "rssFeedTypeId"
      );
      return {
        ...prev,
        filters: [...withoutPropFilters, ...baseFiltersFromProps],
      };
    });
  }, [baseFiltersFromProps]);

  const styles: IControlStyles = props.controlStyles ?? useControlStyles();
  const rssFeedGridQuery = RssFeedService.gridQuery(searchQuery);
  const isGridLoading =
    rssFeedGridQuery.isLoading || rssFeedGridQuery.isFetching;

  const rssFeeds: GridRssFeedDto[] = rssFeedGridQuery.data?.result ?? [];
  const hasData: boolean = rssFeeds.length > 0;

  const actionColumn = React.useMemo(
    () =>
      createActionColumn(styles, DATA_ID_FIELD, [
        GRID_ACTION_TYPES.EDIT,
        GRID_ACTION_TYPES.VIEW,
      ]),
    [styles]
  );

  const layoutOptions = React.useMemo(
    () => createLayoutOptions(hasData),
    [hasData]
  );

  const handleSearch = (values: RssFeedSearchProps): void => {
    setSearchQuery((prev) => {
      let nextFilters = prev.filters.filter((f) => f.field !== "name");

      const trimmed = values.name?.trim();
      if (trimmed) {
        nextFilters.push({
          field: "name",
          operator: SearchOperator.Contains,
          value: trimmed,
        });
      }

      return { ...prev, filters: nextFilters };
    });
  };

  const handleClear = (): void => {
    setSearchQuery((prev) => ({
      ...prev,
      filters: prev.filters.filter((f) => f.field !== "name"),
    }));
  };

  const handleNew = (): void | undefined =>
    props.onCustomEvent?.(GRID_ACTION_TYPES.NEW, Constants.emptyGuid);

  const searchContainer: React.JSX.Element = (
    <SearchContainer
      initialValues={{
        name: Constants.emptyString,
        communityId: props?.communityId ?? Constants.emptyString,
        rssFeedTypeId: props?.rssFeedTypeId,
      }}
      onSearch={handleSearch}
      onClear={handleClear}
    >
      {!props.communityId && (
        <Col lg={3}>
          <Form.Dropdown
            name="communityId"
            label="Community"
            blankOption={{
              key: Constants.emptyGuid,
              display: "Select Community",
              value: Constants.emptyString,
            }}
            options={props.lookups?.communities}
          />
        </Col>
      )}
      {!props.rssFeedTypeId && (
        <Col lg={3} className={!props.communityId ? "ms-2" : "ms-0"}>
          <Form.Dropdown
            name="rssFeedTypeId"
            label="RSS Feed Type"
            blankOption={{
              key: Constants.emptyGuid,
              display: "Select RSS Feed Type",
              value: Constants.emptyString,
            }}
            options={getRssFeedOptions()}
          />
        </Col>
      )}
      <Col
        lg={3}
        className={!props.rssFeedTypeId || !props.communityId ? "ms-2" : "ms-0"}
      >
        <Form.Text name="name" label="Name" />
      </Col>
    </SearchContainer>
  );

  const rssFeedGrid: React.JSX.Element = (
    <>
        <Control.DataGrid
          dataId={DATA_ID_FIELD}
          cols={gridColumns}
          actionCol={actionColumn}
          layoutOptions={layoutOptions}
          onCustomEvent={props.onCustomEvent}
          data={rssFeeds}
          totalRecords={
            rssFeedGridQuery?.data?.totalSize ??
            rssFeedGridQuery?.data?.result?.length ??
            0
          }
          query={searchQuery}
          loading={isGridLoading}
          onQueryChange={(patch) => {
            setSearchQuery((query) => ({
              ...query,
              ...patch,
              sorts: patch.sortBy ? [patch.sortBy] : query.sorts,
            }));
          }}
          uiOptions={{
            title: props.hideTitle ? undefined : `${props.title}s`,
            filterContent: searchContainer,
            primaryAction: {
              text: "Add RSS Feed",
              icon: "bi bi-plus-lg",
              onClick: handleNew,
            },
          }}
        />
    </>
  );

  const rssFeedPageContent: React.JSX.Element = (
    <>
      {rssFeedGrid}
    </>
  );

  return props.hideTitle ? (
    rssFeedPageContent
  ) : (
    <PageContent hideHeader>{rssFeedPageContent}</PageContent>
  );
};
